import type { Reports } from './reports.type.ts';
import type {
  SearchConfig,
  SearchConfigFilters,
  ReportMetadata,
  ReportScope,
  ChartMetadata,
  ChartSettings,
  TableSettings,
  AggregationTableSettings,
  AggregationMetric,
  RangeBucket,
  PartnerAccessConfig,
} from './reports.type.ts';

export type {
  SearchConfig,
  SearchConfigFilters,
  ReportMetadata,
  ReportScope,
  ChartMetadata,
  ChartSettings,
  TableSettings,
  AggregationTableSettings,
  AggregationMetric,
  RangeBucket,
  PartnerAccessConfig,
};
export interface ReportsExtension extends Reports {}
export class ReportsExtension {
  // Computed properties
  get isPrivate(): boolean {
    return this.visibilityLevel === 'private';
  }

  get isPartnerShared(): boolean {
    return this.visibilityLevel === 'partner';
  }

  get isGlobal(): boolean {
    return this.visibilityLevel === 'global';
  }

  get isChartBuilder(): boolean {
    return this.reportType === 'chart_builder';
  }

  get isTableBuilder(): boolean {
    return this.reportType === 'table_builder';
  }

  get isDashboard(): boolean {
    return this.reportType === 'dashboard';
  }

  // Scope (published state) properties
  get scope(): ReportScope {
    return this.metadata?.scope === 'partner_admin' ? 'partner_admin' : 'admin_only';
  }

  get isPublished(): boolean {
    return this.scope === 'partner_admin';
  }

  get isUnpublished(): boolean {
    return !this.isPublished;
  }

  get publishedAt(): string | undefined {
    return this.metadata?.publishedAt;
  }

  get publishedCompanyId(): string | undefined {
    return this.metadata?.publishedCompanyId;
  }

  /**
   * PARTNER-channel state only. Use `publishChannelsLabel` (tio-reporting
   * utils/publish-channels) anywhere a report may also be published to the
   * internal channel — this getter calls such a report "Unpublished".
   */
  get scopeLabel(): string {
    return this.isPublished ? 'Published' : 'Unpublished';
  }

  // The API omits these fields for non-internal viewers.
  get isInternalPublished(): boolean {
    return Boolean(this.internalPublishedAt);
  }

  get isSqlBacked(): boolean {
    return Boolean(this.sqlBacked);
  }

  get isArchived(): boolean {
    return Boolean(this.metadata?.archivedAt);
  }

  get archivedAt(): string | undefined {
    return this.metadata?.archivedAt;
  }

  get serializedQuery(): string | undefined {
    return this.metadata?.serializedQuery;
  }

  get displayName(): string {
    return this.name;
  }

  get formattedUpdatedAt(): string {
    if (!this.updatedAt) return '';
    return new Date(this.updatedAt).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  get visibilityLabel(): string {
    switch (this.visibilityLevel) {
      case 'private':
        return 'Private';
      case 'partner':
        return 'Custom Partner Report';
      case 'global':
        return 'Global';
      default:
        return 'Unknown';
    }
  }
}

const Registration = {
  kind: 'object',
  name: 'report',
  features: ReportsExtension,
};
export default Registration;
